// Styles
import './styles/index.scss';

import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import {
  Layout,
  DealerDivisionGuard,
  AuthRouteGuard,
  BasicHeader,
  BasicDivisionFooter,
  DealerAccessHeader,
} from '@gplibraries/react/components';
import DealerAccessSidebar from './components/Sidebar/DealerAccessSidebar.jsx';

// Views
import LoginPage from './views/LoginPage/LoginPage.jsx';
import LogoutPage from './views/LogoutPage/LogoutPage.jsx';
import AuthRedirect from './views/AuthRedirect/AuthRedirect.jsx';
import { PageNotFound } from '@gplibraries/react/components';
import Home from './views/Home/Home.jsx';
import Article from './views/Article/ArticlePage.jsx';
import Bulletins from './views/Bulletins/Bulletins.jsx';
import EditCategories from './views/Categories/EditCategories.jsx';
import BargainWarehouse from './views/BargainWarehouse/BargainWarehouse.jsx';
import BargainWarehouseListing from './views/BargainWarehouse/BargainWarehouseListing.tsx';
import BargainWarehousePartListing from './views/BargainWarehouse/BargainWarehousePartListing.tsx';
import ManageEmployeeUsers from './views/ManageEmployeeUsers/ManageEmployeeUsers.jsx';
import DealerPasswordChange from './views/DealerPasswordChange/DealerPasswordChange';
import ForgotPassword from './views/ForgotPassword/ForgotPassword.jsx';
import DealerMorphPage from './views/DealerMorphPage/DealerMorphPage.jsx';
import WrongDivision from './views/WrongDivision/WrongDivision.jsx';
import RviRedirect from './views/RviRedirect/RviRedirect.jsx';

// Bootstrap
import createConfig from './bootstrap/createConfig.js';
import createLogger from './bootstrap/createLogger.js';
import initializeMsal from './bootstrap/msal.js';
import initializeQueryClient from './bootstrap/queryClient.js';
import Providers from './providers/Providers.jsx';
import datadogInit from './bootstrap/datadogInit.js';

// Bootstrap dependencies
datadogInit();
const appConfig = createConfig();
const logger = createLogger();
const authClient = await initializeMsal(appConfig.authConfig);
const queryClient = initializeQueryClient();

const roles = appConfig.identity.roles;
const groups = appConfig.identity.groups;

const App = (
  <Providers
    authClient={authClient}
    appConfig={appConfig}
    queryClient={queryClient}
    logger={logger}
  >
    <BrowserRouter>
      <Routes>

        {/* For authenticated routes, make sure dealers aren't on the wrong division */}
        <Route element={<DealerDivisionGuard redirectPath={'wrong-division'}/>}>

          {/*Content with sidebar*/}
          <Route element={<Layout sidebar={<DealerAccessSidebar/>} header={<DealerAccessHeader/>} footer={<BasicDivisionFooter/>}/>} >

            <Route element={<AuthRouteGuard/>}>
              <Route index element={(<Home/>)}/>
              <Route path={'article/:nid/:title?'} element={<Article/>}/>
            </Route>

            <Route element={<AuthRouteGuard
              scopes={[appConfig.dealerAccessApiScope]}
              roles={[roles.CS_USER]}
              groups={[groups.DEALERS, groups.ADMINS]}
            />}>
              <Route path={'manage-employee-users'} element={<ManageEmployeeUsers/>}/>
            </Route>

            <Route element={<AuthRouteGuard
              scopes={[appConfig.dealerAccessApiScope]}
              roles={[roles.CS_USER]}
              groups={[groups.DEALERS, groups.DEALER_PEOPLE, groups.ADMINS]}
            />}>
              <Route path={'change-password'} element={<DealerPasswordChange/>}/>
            </Route>

          </Route>

          {/*Content without sidebar*/}
          <Route element={<Layout header={<DealerAccessHeader/>} footer={<BasicDivisionFooter/>}/>} >

            <Route element={<AuthRouteGuard/>} >
              <Route path={'bargain-warehouse'} element={<BargainWarehouse/>}/>
              <Route path={'bargain-warehouse/parts/:nid/:title?'} element={<BargainWarehousePartListing/>}/>
              <Route path={'bargain-warehouse/:nid/:title?'} element={<BargainWarehouseListing/>}/>
              <Route path={'bulletins'} element={<Bulletins/>}/>
              <Route path={'wrong-division'} element={<WrongDivision/>} />
              <Route path={'rvi'} element={<RviRedirect/>} />
              <Route path={"*"} element={<PageNotFound/>} />
            </Route>

            <Route element={<AuthRouteGuard scopes={[appConfig.drupalScope]} roles={['power_user']}/>} >
              <Route path={'categories/edit'} element={(<EditCategories/>)} />
            </Route>

            {/* Dealer morph page */}
            <Route element={<AuthRouteGuard
              scopes={[appConfig.dealerAccessApiScope]}
              groups={[groups.DEALER_PEOPLE]}
              roles={[roles.CS_USER, roles.TM]}
            />}>
              <Route path={'change-dealer'} element={<DealerMorphPage />} />
            </Route>

          </Route>

        </Route>

        {/*
          Attempts to go to the legacy dealer access home page are redirected to
          this home page. Any paths beyond this first segment should be handled
          by the proxy.
        */}
        <Route path={'/dealer_access'} element={<Navigate to={'/'} replace={true}/>}/>

        {/* Unauthenticated Routes*/}
        <Route element={<Layout header={<BasicHeader/>} footer={<BasicDivisionFooter/>}/>}>
          <Route path={'auth'} element={<AuthRedirect/>} />
          <Route path={'login'} element={<LoginPage/>} />
          <Route path={'logout'} element={<LogoutPage/>} />
          <Route path={'forgot-password'} element={<ForgotPassword/>} />
        </Route>

      </Routes>
    </BrowserRouter>
  </Providers>
)

export default App;